/**
 * Noticing that a body was edited outside the visual editor.
 *
 * The design document travels inside the body, so the two can drift apart: someone
 * edits the Twig in code mode, or through the API, and the envelope still describes
 * the *previous* shape. Rebuilding from the stale document would then silently throw
 * the hand-written Twig away — the `detached` state exists to stop that, and this is
 * how it gets entered for edits the editor did not make itself.
 *
 * **Why the comparison is whitespace-insensitive.** The obvious test is
 * `compile(document) !== storedBody`, and it is wrong now: the compiler started
 * indenting its own output (G2), so *every* body written before that differs from
 * what the same document compiles to today. Byte comparison would flag all of them
 * as hand-edited and detach templates nobody touched.
 *
 * Collapsing whitespace runs ignores formatting while still catching any change to
 * a tag, an argument or a piece of content. The cost is that a whitespace-only hand
 * edit goes unnoticed — which is the right way round: missing a cosmetic edit is
 * harmless, while falsely detaching a template disables its visual editing.
 */
import {
  BLOCK_MARKER_PATTERN,
  COMPILE_WARNING,
  CompileResult,
} from './compile';
import { stripEnvelope } from './envelope';

/**
 * Comparable form of a body: no envelope, **no block markers**, whitespace runs
 * collapsed, trimmed.
 *
 * Whitespace *between* tags carries no meaning in the emitted markup — the compiler
 * decides it — so it cannot distinguish two bodies that mean the same thing.
 *
 * The `<!--cvn:… -->` markers are stripped for the same reason, and for a sharper one:
 * they were introduced *after* templates were already stored, so every existing visual
 * template's body lacks them while its document now compiles with them. Comparing
 * bytes would declare all of those hand-edited on their next open — the exact failure
 * this module exists to prevent. Like whitespace, the markers are compiler-owned, so a
 * difference in them is never a user's edit.
 */
export function normalizeBodyForComparison(body: string): string {
  return canonicalizeInterpolation(
    stripEnvelope(body)
      .replace(BLOCK_MARKER_PATTERN, '')
      .replace(/\s+/g, ' ')
      .trim(),
  );
}

/**
 * `'Hi ' ~ (customer.firstname)` and `'Hi {{ customer.firstname }}'` are the same
 * text, emitted two different ways.
 *
 * The compiler used to concatenate every variable in a text prop, and now keeps a
 * **send-time** one as literal text instead — concatenating it made our own push
 * render resolve it, against a payload with no recipient in it, so the variable
 * arrived empty at the provider. Same reason as the block markers above: the
 * choice is compiler-owned and changed after templates were already stored, so a
 * body still carrying the old form is not a hand edit and must not be detached
 * for it.
 *
 * Only a parenthesised simple expression is folded — the exact shape the compiler
 * emits. Anything with its own parentheses in it (`(a|default('x'))`) is left
 * alone and compares as before, which errs toward calling a body diverged rather
 * than silently rebuilding over a real hand edit.
 */
function canonicalizeInterpolation(body: string): string {
  return (
    body
      // 'a' ~ (x) ~ 'b'  →  'a{{ x }}b'   (the two literals merge into one)
      .replace(/'\s*~\s*\(([^()']*)\)\s*~\s*'/g, '{{ $1 }}')
      // 'a' ~ (x)  →  'a{{ x }}'
      .replace(/'\s*~\s*\(([^()']*)\)/g, "{{ $1 }}'")
      // (x) ~ 'b'  →  '{{ x }}b'
      .replace(/\(([^()']*)\)\s*~\s*'/g, "'{{ $1 }}")
  );
}

/**
 * True when the stored body is not what the document would produce — i.e. it was
 * edited by hand.
 *
 * Both arguments may carry an envelope; it is stripped from each, because the
 * envelope's own bytes change whenever the document does and would make every
 * comparison differ.
 */
export function bodiesDiverge(
  storedBody: string,
  compiledBody: string,
): boolean {
  return (
    normalizeBodyForComparison(storedBody) !==
    normalizeBodyForComparison(compiledBody)
  );
}

/**
 * Who wrote the stored body — with `undecidable` for "not yet answerable".
 *
 * - `editor` — the body is what this document compiles to.
 * - `hand-edited` — it is not, so someone edited it outside the visual editor.
 * - `undecidable` — the compiler could not emit every block, so a difference proves
 *   nothing. **Do not detach, and ask again later.**
 */
export type BodyOrigin = 'editor' | 'hand-edited' | 'undecidable';

/**
 * The fragment catalog arrives asynchronously, and until it does the registry has no
 * definition for the document's own components — those blocks emit *nothing* and
 * warn `unsupported-block`. Comparing that stump against the stored body says only
 * "the catalog is not loaded yet", so answering `hand-edited` there declares a
 * template hand-edited on its own first open. Observed exactly that: a freshly
 * generated template detached on a cold open and did not on the second.
 *
 * The same guard covers a genuinely missing fragment, where detaching would be
 * worst: the body is intact and the *editor* is missing a piece, so disabling visual
 * editing would be the wrong response. `preflight` reports the missing key instead.
 *
 * Takes the whole `CompileResult` rather than a body and a warning list, so the
 * markup and the warnings that describe it cannot be passed from different compiles.
 */
export function judgeBodyOrigin(
  storedBody: string,
  compiled: CompileResult,
): BodyOrigin {
  if (storedBody.trim() === '') return 'editor';

  const unemittable = compiled.warnings.some(
    (warning) => warning.code === COMPILE_WARNING.UNSUPPORTED_BLOCK,
  );
  if (unemittable) return 'undecidable';

  return bodiesDiverge(storedBody, compiled.body) ? 'hand-edited' : 'editor';
}
